/**
 * useModularFormInstances.js
 *
 * Shared hook for subcollection-based form instance storage. Both Adult Autism
 * patients and informants store questionnaire answers in individual formInstance
 * documents rather than a monolithic formData blob.
 *
 * The hook owns:
 * - header + formInstances state
 * - localStorage hydration for fast first paint
 * - blocking Firestore writes with optimistic local state updates
 * - rollback on write failure via reloadFormInstance
 *
 * Write model: callers await updateFormAnswers() which performs an optimistic
 * local update (React state + localStorage) then awaits the Firestore write.
 * This matches ADHD's blocking save pattern for data integrity — users always
 * know their answer persisted before advancing.
 *
 * Callers supply path-specific fetch/write functions so patient and informant
 * contexts can reuse the same state logic without duplicating it.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { emitFormStarted, emitFormSaved, emitFormSaveFailed, emitFormCompleted } from '../analytics/serviceUserFormEvents';
import { MODULAR_FORM_INSTANCE_WRITABLE_FIELDS } from '../constants/modularFormInstanceFields';

/**
 * Development-only guard: warn if a caller passes a field in `extraFields` that
 * the Firestore rules whitelist does not permit. Such a field would cause the
 * ENTIRE update to be silently rejected in production (the rules use hasOnly),
 * so catching it here during development prevents another "answer silently
 * failed to save" regression. No-op in production builds.
 */
function warnOnUnwritableFields(extraFields) {
  if (process.env.NODE_ENV === 'production') return;
  const unwritable = Object.keys(extraFields || {}).filter(
    (key) => !MODULAR_FORM_INSTANCE_WRITABLE_FIELDS.includes(key)
  );
  if (unwritable.length > 0) {
    console.warn(
      `[useModularFormInstances] extraFields contains field(s) not in the ` +
      `Firestore rules whitelist: ${unwritable.join(', ')}. This write will be ` +
      `rejected in production. Add the field to both firestore.rules and ` +
      `src/constants/modularFormInstanceFields.js if it is legitimately writable.`
    );
  }
}

/**
 * @param {object} options
 * @param {boolean} options.enabled - When false, no fetches run. Data is only
 *   cleared on sign-out (entityId becomes null) or user switch (entityId changes),
 *   NOT on transient enabled=false during claims propagation.
 * @param {string|null} options.entityId - Auth uid (patient or informant)
 * @param {string} options.localStorageKey - Distinct key per context to avoid collisions
 * @param {() => Promise<{header: object, formInstances: object}>} options.fetchData
 * @param {(formKey: string) => import('firebase/firestore').DocumentReference} options.getFormInstanceDocRef
 * @param {{role: 'patient'|'informant', assessmentType: 'adhd'|'autism', subjectId?: string}} [options.analyticsContext]
 *   When provided, form-filling milestone analytics (started/saved/completed)
 *   are emitted on successful writes; omitted means no analytics.
 *   `subjectId` names the person the forms are ABOUT when that differs from the
 *   account filling them in — an informant answers about a patient. Without it
 *   informant events would describe the informant as their own subject; the
 *   informant is still recorded as the envelope's actor.
 */
export function useModularFormInstances({
  enabled,
  entityId,
  localStorageKey,
  fetchData,
  getFormInstanceDocRef,
  analyticsContext,
}) {
  const [header, setHeader] = useState(() => {
    if (!enabled) return null;
    try {
      const stored = localStorage.getItem(localStorageKey);
      return stored ? JSON.parse(stored).header || null : null;
    } catch {
      return null;
    }
  });

  const [formInstances, setFormInstances] = useState(() => {
    if (!enabled) return {};
    try {
      const stored = localStorage.getItem(localStorageKey);
      return stored ? JSON.parse(stored).formInstances || {} : {};
    } catch {
      return {};
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Who the milestone events are ABOUT. For a patient this is the account
  // itself; for an informant it is the patient they answer about, so the
  // stream describes one journey per patient rather than one per account.
  const analyticsSubjectId = analyticsContext?.subjectId || entityId;

  const headerRef = useRef(header);
  headerRef.current = header;
  const prevEntityIdRef = useRef(entityId);

  const persistToStorage = useCallback((headerData, formInstancesData) => {
    if (!enabled) return;
    try {
      localStorage.setItem(localStorageKey, JSON.stringify({
        header: headerData,
        formInstances: formInstancesData,
      }));
    } catch {
      // Non-critical — quota exceeded etc.
    }
  }, [enabled, localStorageKey]);

  const clearData = useCallback(() => {
    setHeader(null);
    setFormInstances({});
    setError(null);
    localStorage.removeItem(localStorageKey);
  }, [localStorageKey]);

  const reloadFormInstance = useCallback(async (formKey) => {
    if (!entityId || !getFormInstanceDocRef) return;
    try {
      const fiRef = getFormInstanceDocRef(formKey);
      const fiDoc = await getDoc(fiRef);
      if (fiDoc.exists()) {
        setFormInstances((prev) => {
          const rolled = { ...prev, [formKey]: fiDoc.data() };
          persistToStorage(headerRef.current, rolled);
          return rolled;
        });
      }
    } catch {
      // Re-fetch failure is non-critical; next full load will fix.
    }
  }, [entityId, getFormInstanceDocRef, persistToStorage]);

  const fetchEntityData = useCallback(async () => {
    if (!enabled || !entityId) return;

    setLoading(true);
    setError(null);

    try {
      const { header: headerData, formInstances: formInstancesData } = await fetchData();
      setHeader(headerData);
      setFormInstances(formInstancesData);
      persistToStorage(headerData, formInstancesData);
    } catch (err) {
      console.error('[useModularFormInstances] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [enabled, entityId, fetchData, persistToStorage]);

  // Core lifecycle effect: manages data clearing and fetching based on auth state.
  // Only clears data on sign-out (entityId null) or user switch (entityId changed).
  // Does NOT clear on transient enabled=false (e.g. during claims propagation
  // window after login) to prevent wiping valid cached data before claims arrive.
  useEffect(() => {
    const entityIdChanged = prevEntityIdRef.current !== entityId;
    prevEntityIdRef.current = entityId;

    if (!entityId) {
      clearData();
      return;
    }

    if (entityIdChanged) {
      clearData();
    }

    if (!enabled) {
      return;
    }

    fetchEntityData();
  }, [enabled, entityId, fetchEntityData, clearData]);

  const updateFormAnswers = useCallback(async (formKey, answerUpdates, extraFields = {}) => {
    if (!enabled || !entityId) return;

    warnOnUnwritableFields(extraFields);

    const existing = formInstances[formKey] || {};
    const mergedAnswers = { ...existing.answers, ...answerUpdates };

    // Write-once lifecycle timestamps for admin/portal analytics parity with
    // ADHD's formData.{formKey}.firstStartedAt / firstCompletedAt pattern.
    // Each field is set exactly once (the first save that meets the condition)
    // and never overwritten on subsequent saves, so they anchor billing and
    // time-to-start / time-to-complete metrics to a stable point.
    const firestoreTimestamps = {};
    const localTimestamps = {};

    const newProgress = extraFields.progress;
    const newStatus = extraFields.status;

    // firstStartedAt / startedAt: record when the patient first engages with
    // this form (progress moves above zero). Mirrors ADHD's firstStartedAt
    // which is set in PatientFormTemplate on first form access.
    if (typeof newProgress === 'number' && newProgress > 0 && !existing.firstStartedAt) {
      firestoreTimestamps.firstStartedAt = serverTimestamp();
      firestoreTimestamps.startedAt = serverTimestamp();
      localTimestamps.firstStartedAt = new Date().toISOString();
      localTimestamps.startedAt = new Date().toISOString();
    }

    // completedAt: record when the form first reaches 100% completion. Write-
    // once so re-edits after completion don't shift the timestamp. Mirrors
    // ADHD's firstCompletedAt at the stage level.
    if (newStatus === 'completed' && !existing.completedAt) {
      firestoreTimestamps.completedAt = serverTimestamp();
      localTimestamps.completedAt = new Date().toISOString();
    }

    const updated = { ...existing, answers: mergedAnswers, ...extraFields, ...localTimestamps };

    // Optimistic local update — UI reflects the change immediately before
    // the Firestore round-trip completes (React state + localStorage).
    const newInstances = { ...formInstances, [formKey]: updated };
    setFormInstances(newInstances);
    persistToStorage(headerRef.current, newInstances);

    // Await Firestore write — callers block on this for data integrity,
    // matching ADHD's transactional save-before-advance pattern. The
    // firestoreTimestamps use serverTimestamp() sentinels (resolved server-
    // side) while the optimistic local state above uses ISO strings.
    const fiRef = getFormInstanceDocRef(formKey);
    try {
      await updateDoc(fiRef, {
        answers: mergedAnswers,
        ...extraFields,
        ...firestoreTimestamps,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(`[useModularFormInstances] Write failed for ${formKey}:`, err);
      // Failed-save analytics: error code only, throttled per form. This is
      // the exact failure mode behind "completed but unscored" regressions,
      // and the not-client-side corroboration signal for feedback triage.
      // Belt-and-braces try/catch: an analytics throw here would replace the
      // original error the caller needs to surface.
      try {
        if (analyticsContext) {
          emitFormSaveFailed(
            { ...analyticsContext, patientId: analyticsSubjectId, formKey },
            { errorCode: err?.code || err?.name },
          );
        }
      } catch (_) {
        // Analytics must never affect the save path.
      }
      await reloadFormInstance(formKey);
      throw err;
    }

    // Milestone analytics, only after the write succeeded (fire-and-forget;
    // saves are throttled per form inside the helper). The write-once
    // timestamp fields double as the started/completed edge detectors.
    // Belt-and-braces try/catch: this runs after the awaited write, so an
    // accidental synchronous throw would reject updateFormAnswers AFTER the
    // data persisted - a false "save failed" on an integrity-sensitive path.
    try {
      if (analyticsContext) {
        const eventContext = { ...analyticsContext, patientId: analyticsSubjectId, formKey };
        // Modular progress is a 0..1 fraction (see GenericFormPage); events
        // carry whole percentages.
        const progressPct =
          typeof newProgress === 'number'
            ? Math.max(0, Math.min(100, Math.round(newProgress * 100)))
            : undefined;
        if (firestoreTimestamps.firstStartedAt) emitFormStarted(eventContext);
        if (firestoreTimestamps.completedAt) {
          emitFormCompleted(eventContext, { progressPct });
        } else if (!firestoreTimestamps.firstStartedAt) {
          emitFormSaved(eventContext, { progressPct });
        }
      }
    } catch (_) {
      // Analytics must never affect the save path.
    }
  }, [enabled, entityId, formInstances, getFormInstanceDocRef, persistToStorage, reloadFormInstance, analyticsContext]);

  return {
    header,
    formInstances,
    loading,
    error,
    fetchEntityData,
    updateFormAnswers,
    clearData,
  };
}
